import { css } from "@emotion/react";
import cn from "classnames";
import React from "react";
import { NoImageSvg, PercentSvg } from "../../assets";
import { ContentfulImage } from "../../components/_shared-components/image-related/ContentfulImage";
import { SwarovskiLink } from "../../components/_shared-components/ui-elements/SwarovskiLink";
import { EXPERIMENTAL_TextWithProductNameRenderer } from "../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { CSS_MQ } from "../../styles/breakpoints";
import { FONTS, fontSize, fontStyles } from "../../styles/fonts";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { type PDPCarouselProduct } from "./mapProduct";
import { T } from "../../components/T";

export const CarouselProductView: React.FC<{ product: PDPCarouselProduct }> = ({ product }) => {
  return (
    <SwarovskiLink
      link={product.path}
      isInternal={true}
      scrollToTop={true}
      styled={"no-decoration"}
      key={product.key}
      className={cn({ selected: product.isSelected })}
      disabled={product.isSelected}
      attachStyles={linkStyles}
    >
      <div css={wrapperStyles}>
        <div
          className={"u-relative u-d-flex u-jc-center u-ai-center"}
          css={css`
            aspect-ratio: 4/3;
          `}
        >
          {product.isDiscounted && (
            <div
              css={css`
                position: absolute;

                top: -10px;
                right: -14px;
                border-top-right-radius: 8px;

                width: 40px;
                height: 40px;

                z-index: 1;

                background-color: ${CSS_COLORS.Green};
                color: ${CSS_COLORS.White};

                display: flex;
                align-items: center;
                justify-content: center;

                svg {
                  width: 20px;
                  height: 20px;
                }
              `}
            >
              <PercentSvg />
            </div>
          )}

          {product.image ? (
            <ContentfulImage
              assetId={product.image.sys.id}
              contentfulLink={{
                url: product.image.url,
                noTransparentBackground: false,
                requestedWidth: 300,
              }}
              css={css`
                object-fit: contain;
                object-position: center;
                width: 100%;
              `}
              attachPictureStyles={css`
                display: flex;
                max-height: 100%;
                max-width: 100%;
                overflow: hidden;

                position: absolute;
              `}
            />
          ) : (
            <span
              css={[
                FONTS.SwarovskiFutura300,
                fontSize.x2s,
                css`
                  color: ${CSS_COLORS.GrayDark2024};
                  display: block;
                  position: absolute;
                `,
              ]}
            >
              <NoImageSvg strokeWidth={2} />
            </span>
          )}
        </div>

        <div
          css={css`
            flex-grow: 1;

            display: flex;
            justify-content: center;
            align-items: flex-start;
          `}
        >
          <div className={"u-ta-center"}>
            <span css={productNameStyles}>
              <EXPERIMENTAL_TextWithProductNameRenderer text={product.name} />
            </span>

            <div css={startingPriceStyles}>
              <T>{product.startingPriceLabel}</T>
            </div>
          </div>
        </div>
      </div>
    </SwarovskiLink>
  );
};

const startingPriceStyles = [
  FONTS.SwarovskiFutura300,
  fontSize.x2s,
  css`
    color: black;
    margin: 0;
  `,
];

const productNameStyles = [
  fontStyles.headline5,
  css`
    margin: 0;
    display: block;
  `,
];

const wrapperStyles = css`
  &:hover,
  .selected & {
    background-color: ${CSS_COLORS.GreenWhite};
  }

  padding: 8px 12px;
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const linkStyles = css`
  display: flex;
  flex-shrink: 0;

  --w: 200px;
  ${CSS_MQ.atLeastLg} {
    --w: 220px;
  }
  ${CSS_MQ.atLeastXl} {
    --w: 240px;
  }

  width: var(--w);

  border-radius: 8px;
  border-color: ${CSS_COLORS.Grey4};
  border-width: 2px;
  border-style: solid;
`;
